import { useGlobals } from "@/utils/globals";
import { RumInit } from "@maersk-global/telemetry-web-sdk";
import { getEnvConfig, Environment } from "@/config/envConfig.ts";
import { useUserProfileStore } from "@/modules/userProfile/store/userProfileStore";
import type { UserProfile } from "@/modules/userProfile/types/UserProfile";

let _rumInstance: ReturnType<typeof RumInit> | undefined;

export function rumInit() {
  // do not create if instance is already created
  if (_rumInstance) {
    return;
  }

  const { version } = useGlobals();
  const ENV_CONFIG = getEnvConfig();
  if (ENV_CONFIG.name === Environment.LOCAL) {
    return;
  }
  if (
    !ENV_CONFIG.rumAppName?.trim() ||
    !ENV_CONFIG.rumApiToken?.trim() ||
    ENV_CONFIG.rumAppName.includes("%VITE_RUM_") ||
    ENV_CONFIG.rumApiToken.includes("%VITE_RUM_")
  ) {
    console.warn("RUM is disabled: configure VITE_RUM_APP_NAME and VITE_RUM_API_TOKEN for this deployment");
    return;
  }
  const userProfileStore = useUserProfileStore();
  const userProfileData = userProfileStore.userProfile;

  // documentation: https://docs.mop.maersk.io/rum/quickstart/getting-started/index.html
  const rumConfig = {
    app: {
      name: ENV_CONFIG.rumAppName,
      environment: ENV_CONFIG.rumAppEnv,
      version
    },
    apiKey: ENV_CONFIG.rumApiToken,
    useSendBeacon: true,
    performanceInstrument: {
      ignoreEvents: ["msal.*", ".*vue.*"]
    },
    propagateTraceHeaderCorsUrls: [
      /^https:\/\/legalfrontdoorapp-(requestregisterservice|requestresolverservice|adminservice)(dev|pp)\.westeurope\.(dev|prod)\.maersk\.io\/?$/
    ], // Back-end APIs for all envs
    ignoreUrls: [`http://localhost:5001/`, `https://legalfrontdoorapp-ui.westeurope.dev.maersk.io/`],
    traceLog: true,
    instrumentations: {
      interactions: {
        eventNames: ["click"] as (keyof HTMLElementEventMap)[] // list here any event we'd like to be captured
      }
    }
  };

  try {
    _rumInstance = RumInit(rumConfig);
    _setUser(userProfileData);
  } catch {
    console.warn("Unable to initialise RUM telemetry");
  }
}

function _setUser(user: UserProfile) {
  if (!_rumInstance) {
    return;
  }
  _rumInstance.api.setUser({
    id: user.userId?.trim() || undefined,
    email: user.mail,
    username: user.fullName
  });
}

export function logRumEvent(eventName: string, attributes: Record<string, unknown>): boolean {
  if (!_rumInstance) {
    const env = getEnvConfig();
    if (env.name !== Environment.LOCAL) {
      console.warn("Unable to submit Ask Legal telemetry: RUM is not initialised");
    }
    return false;
  }

  try {
    const profile = useUserProfileStore().userProfile;
    _setUser(profile);
    const userId = profile.userId?.trim() || null;
    const country = profile.country?.trim() || null;
    const turnId = attributes.turnId;
    const conversationId = attributes.conversationId;
    _rumInstance.api.pushLog([JSON.stringify({ ...attributes, event: eventName, userId, country })], {
      context: {
        eventName,
        ...(typeof turnId === "string" ? { turnId } : {}),
        ...(typeof conversationId === "string" ? { conversationId } : {}),
        ...(userId ? { userId } : {}),
        ...(country ? { country } : {})
      },
      skipDedupe: true
    });
    return true;
  } catch {
    console.warn("Unable to submit Ask Legal telemetry event");
    return false;
  }
}
